<template>
    <div class="l-meeting-feedback-section">
        <!-- Headline -->
        <div
            class="clickable mouseover-parent"
            tabindex="0"
            @click="toggleFeedback()"
            @keyup.enter="toggleFeedback()"
        >
            <span v-if="showFeedback">
                <span class="agenda-question">{{ $t("meetings.labelFeedbackHide") }}</span>
                <i class="fa fa-caret-down color-grey l-default-margin-s mouseover"></i>
            </span>
            <span v-else>
                <span class="agenda-question">{{ $t("meetings.labelFeedback") }}</span>
                <i class="fa fa-caret-right color-grey l-default-margin-s mouseover"></i>
            </span>
        </div>
        <!--/Headline -->

        <!-- List -->
        <div v-if="showFeedback">
            <!-- Other Participants Feedback -->
            <div v-for="participant in otherParticipants" :key="participant._id">
                <div class="tw-mt-4 tw-bg-gray-50 tw-p-3 tw-mb-0 tw-rounded-lg">
                    <l-profile-picture :user="participant" size="s" />

                    <span class="l-default-margin">
                        <span>
                            {{ participant.firstname }}
                            {{ participant.lastname }}
                        </span>
                    </span>
                </div>

                <div
                    v-for="summary in participant.feedback.slice(0, feedbackLimit)"
                    :key="summary._id"
                    class="tw-mt-4 tw-border tw-border-solid tw-p-3 tw-border-gray-200 tw-rounded-lg"
                >
                    <div class="fake-link clickable">
                        <span
                            tabindex="0"
                            class="fake-link"
                            @click="openFeeback({ id: summary._id })"
                            @keyup.enter="openFeeback({ id: summary._id })"
                        >
                            <span>
                                {{
                                    $t("profile.feedback.from", {
                                        firstname: summary.sender.firstname,
                                    })
                                }}
                            </span>
                            <div class="hr-small" />
                            <l-text :text="summary.content" class="tw-line-clamp-3"></l-text>
                        </span>
                    </div>
                </div>

                <l-no-data
                    v-show="!participant.feedback.length"
                    :icon="icon"
                    :text="
                        $t(`meetings.listEmptyFeedbackOpposite`, {
                            firstname: participant.firstname,
                        })
                    "
                    data-test-id="participant-meeting-feedback-list-no-data"
                />

                <l-space :size="1" />
            </div>
            <!--/Other Participants Feedback -->

            <!-- Current User Feedback -->
            <div class="tw-mt-4 tw-bg-gray-50 tw-p-3 tw-mb-0 tw-rounded-lg">
                <l-profile-picture :user="currentUser" size="s" />

                <span class="l-default-margin">
                    <span>
                        {{ currentUser.firstname }}
                        {{ currentUser.lastname }}
                    </span>
                </span>
            </div>

            <div
                v-for="summary in currentUserFeedback.slice(0, feedbackLimit)"
                :key="summary._id"
                class="tw-mt-4 tw-border tw-border-solid tw-p-3 tw-border-gray-200 tw-rounded-lg"
            >
                <div class="fake-link clickable">
                    <span
                        tabindex="0"
                        class="fake-link"
                        @click="openFeeback({ id: summary._id })"
                        @keyup.enter="openFeeback({ id: summary._id })"
                    >
                        <span
                            >{{
                                $t("profile.feedback.from", {
                                    firstname: summary.sender.firstname,
                                })
                            }}
                        </span>
                        <div class="hr-small" />
                        <l-text :text="summary.content" class="tw-line-clamp-3"></l-text>
                    </span>
                </div>
            </div>

            <l-no-data
                v-show="!currentUserFeedback.length"
                :icon="icon"
                :text="
                    $t(`meetings.listEmptyFeedbackOpposite`, {
                        firstname: currentUser.firstname,
                    })
                "
                data-test-id="participant-meeting-feedback-list-no-data"
            />
            <!--/Current User Feedback -->

            <l-space :size="1" />
        </div>
        <!--/List -->
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import LNoData from "@/components/LNoData";
import LProfilePicture from "@/components/LProfilePicture";
import LSpace from "@/components/LSpace";
import LText from "@/components/LText";

import icon from "@/img/icons/feedback.png";

export default {
    components: {
        LNoData,
        LProfilePicture,
        LSpace,
        LText,
    },
    props: {
        meeting: {
            type: Object,
            required: true,
        },
        meetingModeEnabled: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            showFeedback: false,
            feedbackLimit: 6,
        };
    },
    computed: {
        ...mapGetters({
            feedbackList: "feedback/feedback/listByUser",
            currentUser: "users/current/user",
        }),
        otherParticipants() {
            return this.meeting.otherUsers.map(participant => {
                const feedbackListForParticipant = this.feedbackList[participant._id] || [];

                return {
                    ...participant,
                    feedback: this.meetingModeEnabled
                        ? feedbackListForParticipant.filter(
                              feedback => feedback.visibility !== "receiver",
                          )
                        : feedbackListForParticipant,
                };
            });
        },
        currentUserFeedback() {
            if (this.meetingModeEnabled) {
                return this.feedbackList[this.currentUser._id].filter(
                    feedback => feedback.visibility !== "receiver",
                );
            }

            return this.feedbackList[this.currentUser._id];
        },
    },
    created() {
        this.icon = icon;
    },
    methods: {
        async toggleFeedback() {
            const showFeedback = !this.showFeedback;

            if (showFeedback) {
                const users = [...this.meeting.otherUsers, this.currentUser].map(participant =>
                    participant._id ? participant._id : participant,
                );

                await Promise.all(
                    users.map(userId =>
                        this.$store.dispatch({
                            type: "feedback/feedback/getListByUser",
                            payload: { userId },
                        }),
                    ),
                );
            }

            this.showFeedback = showFeedback;
        },
        openFeeback({ id }) {
            const url = `${window.location.protocol}//${window.location.host}${window.location.pathname}#/feedback/list/all/details/${id}`;
            window.open(url, "_blank", "noopener noreferrer");
        },
    },
};
</script>

<style scoped></style>
